import { getAccessToken } from '../lib/authSession';
/**
 * WebAuthn Service for Biometric Authentication
 * Supports FaceID, TouchID, Windows Hello, and hardware security keys
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WebAuthn configuration
const RP_NAME = 'NotaryOS';
const RP_ID = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

class WebAuthnService {
  constructor() {
    this.isSupported = this.checkSupport();
  }

  /**
   * Check if WebAuthn is supported
   */
  checkSupport() {
    return !!(
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential === 'function'
    );
  }

  /**
   * Check if platform authenticator (biometric) is available
   */
  async isPlatformAuthenticatorAvailable() {
    if (!this.isSupported) return false;
    
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Error checking platform authenticator:', error);
      return false;
    }
  }

  /**
   * Get available authenticator types
   */
  async getAvailableAuthenticators() {
    const authenticators = [];
    
    if (!this.isSupported) {
      return authenticators;
    }

    try {
      const platformAvailable = await this.isPlatformAuthenticatorAvailable();
      
      if (platformAvailable) {
        // Detect specific biometric type based on platform
        const userAgent = navigator.userAgent.toLowerCase();
        
        if (/iphone|ipad|ipod/.test(userAgent)) {
          authenticators.push({ type: 'platform', name: 'Face ID / Touch ID', icon: 'fingerprint' });
        } else if (/mac/.test(userAgent)) {
          authenticators.push({ type: 'platform', name: 'Touch ID', icon: 'fingerprint' });
        } else if (/android/.test(userAgent)) {
          authenticators.push({ type: 'platform', name: 'Huella Digital / Reconocimiento Facial', icon: 'fingerprint' });
        } else if (/windows/.test(userAgent)) {
          authenticators.push({ type: 'platform', name: 'Windows Hello', icon: 'scan-face' });
        } else {
          authenticators.push({ type: 'platform', name: 'Biometría del Dispositivo', icon: 'fingerprint' });
        }
      }
      
      // Security keys are always an option if WebAuthn is supported
      authenticators.push({ type: 'cross-platform', name: 'Llave de Seguridad USB', icon: 'key' });
      
    } catch (error) {
      console.error('Error getting authenticators:', error);
    }
    
    return authenticators;
  }

  /**
   * Convert base64url to ArrayBuffer
   */
  base64urlToArrayBuffer(base64url) {
    const padding = '='.repeat((4 - base64url.length % 4) % 4);
    const base64 = (base64url + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; i++) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray.buffer;
  }

  /**
   * Convert ArrayBuffer to base64url
   */
  arrayBufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Register a new biometric credential
   */
  async registerCredential(userId, userName, displayName) {
    if (!this.isSupported) {
      throw new Error('WebAuthn no está soportado en este navegador');
    }

    const token = getAccessToken();
    if (!token) {
      throw new Error('Debes iniciar sesión primero');
    }

    try {
      // Get registration options from server
      const optionsResponse = await fetch(`${API_URL}/api/webauthn/register/options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userId,
          user_name: userName,
          display_name: displayName
        })
      });

      if (!optionsResponse.ok) {
        const error = await optionsResponse.json();
        throw new Error(error.detail || 'Error al obtener opciones de registro');
      }

      const options = await optionsResponse.json();

      // Convert challenge and user.id from base64url to ArrayBuffer
      const publicKeyOptions = {
        ...options,
        challenge: this.base64urlToArrayBuffer(options.challenge),
        user: {
          ...options.user,
          id: this.base64urlToArrayBuffer(options.user.id)
        },
        excludeCredentials: (options.excludeCredentials || []).map(cred => ({
          ...cred,
          id: this.base64urlToArrayBuffer(cred.id)
        }))
      };

      // Create credential using WebAuthn
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      });

      // Prepare credential for server
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
          attestationObject: this.arrayBufferToBase64url(credential.response.attestationObject)
        }
      };

      // Verify and store credential on server
      const verifyResponse = await fetch(`${API_URL}/api/webauthn/register/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          credential: credentialData,
          challenge: options.challenge
        })
      });

      if (!verifyResponse.ok) {
        const error = await verifyResponse.json();
        throw new Error(error.detail || 'Error al verificar credencial');
      }

      return await verifyResponse.json();

    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('Registro cancelado o denegado por el usuario');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('Ya tienes una credencial biométrica registrada');
      }
      throw error;
    }
  }

  /**
   * Authenticate using biometric credential
   */
  async authenticate(contractId = null) {
    if (!this.isSupported) {
      throw new Error('WebAuthn no está soportado en este navegador');
    }

    const token = getAccessToken();
    if (!token) {
      throw new Error('Debes iniciar sesión primero');
    }

    try {
      // Get authentication options from server
      const optionsResponse = await fetch(`${API_URL}/api/webauthn/authenticate/options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ contract_id: contractId })
      });

      if (!optionsResponse.ok) {
        const error = await optionsResponse.json();
        throw new Error(error.detail || 'Error al obtener opciones de autenticación');
      }

      const options = await optionsResponse.json();

      // Convert challenge and credential IDs from base64url to ArrayBuffer
      const publicKeyOptions = {
        ...options,
        challenge: this.base64urlToArrayBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map(cred => ({
          ...cred,
          id: this.base64urlToArrayBuffer(cred.id)
        }))
      };

      // Get credential using WebAuthn
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });

      // Prepare assertion for server
      const assertionData = {
        id: assertion.id,
        rawId: this.arrayBufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          clientDataJSON: this.arrayBufferToBase64url(assertion.response.clientDataJSON),
          authenticatorData: this.arrayBufferToBase64url(assertion.response.authenticatorData),
          signature: this.arrayBufferToBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle 
            ? this.arrayBufferToBase64url(assertion.response.userHandle) 
            : null
        }
      };

      // Verify assertion on server
      const verifyResponse = await fetch(`${API_URL}/api/webauthn/authenticate/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          assertion: assertionData,
          challenge: options.challenge,
          contract_id: contractId
        })
      });

      if (!verifyResponse.ok) {
        const error = await verifyResponse.json();
        throw new Error(error.detail || 'Error al verificar autenticación');
      }

      return await verifyResponse.json();

    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('Autenticación cancelada o denegada');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('No hay credenciales biométricas registradas');
      }
      throw error;
    }
  }

  /**
   * Check if user has registered biometric credentials
   */
  async hasRegisteredCredentials() {
    const token = getAccessToken();
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/api/webauthn/credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.credentials && data.credentials.length > 0;
      }
    } catch (error) {
      console.error('Error checking credentials:', error);
    }
    
    return false;
  }

  /**
   * Remove a registered credential
   */
  async removeCredential(credentialId) {
    const token = getAccessToken();
    if (!token) {
      throw new Error('Debes iniciar sesión primero');
    }

    const response = await fetch(`${API_URL}/api/webauthn/credentials/${credentialId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Error al eliminar credencial');
    }

    return true;
  }
}

// Export singleton instance
export const webAuthnService = new WebAuthnService();
export default webAuthnService;
